import React, { Fragment, createContext, useContext, useState, useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import Forward from '../../common/logic/claim/forward.js';
import saveUserData from '../../common/logic/claim/save_user_data.js';
import PageLoader from '../../common/components/page_loader.js';
import Intro from '../../common/components/intro.js';
import Screening from '../../common/components/screening.js';
import Qualified from '../../common/components/qualified.js';
import CustomQualified from '../../common/components/custom_qualified.js';
import GroupIntro from '../../common/components/group_intro.js';
import Group from '../../common/components/metrics/group.js';
import ThoughtBubbleIntro from '../../common/components/thought_bubble_intro.js';
import ThoughtBubble from '../../common/components/metrics/thought_bubble.js';
import Markup from '../../common/components/metrics/markup.js';
import { fraudContext } from "../../common/contexts";
import { LastStepsComponents } from '../../common/logic/last_steps';
import { customMetricsByLocation } from "../../common/logic/eligible";
import WithOpenEnd from '../../common/components/metrics/customMetricsOpenEnd/withOpenEnd';
import runPreload from '../../common/logic/claim/preload';
import ProductIntro from './product_intro.js';

const resultContext = createContext(
  {
    survey_status: '',
    answers: [],
    loops: [],
    claim_answers: [],
    claim_views: [],
    thought_bubbles: []
  }
);

const Index = ({
  data, updateStep, updateQuestionIndex, updateGroupIndex,
  updateRandomClaimIndex, qIndex, rcIndex, gIndex, step,
  loading, changeLoadingState
}) => {
  const {
    save_url,
    demographics, optional_demographics,
    screenings, claims_groups, markup_tool, random_claims,
    translations, qualified_custom_text, image_url, task,
    claim_klass
  } = data;

  const result = useContext(resultContext);
  const { projectId, antiFraud, fraudProcessing, gaTrackingId } = useContext(fraudContext);

  const saveRequest = (status) => {
    saveUserData(save_url, status, result, data);
  };

  const [ forwardEntity ] = useState(new Forward({
    updateStep,
    runSave: (status) => saveRequest(status),
    updateQuestionIndex,
    updateGroupIndex,
    updateRandomClaimIndex
  }
  ));

  const currentQuestion = (questions) => (questions[qIndex]);
  const currentRandomClaim = () => (data.random_claims[rcIndex]);
  const currentGroup = () => (claims_groups[gIndex]);
  const goForward = () => {
    forwardEntity.forward(step, data, result, projectId, antiFraud, gaTrackingId);
  };
  const goNextQuestion = (questions) => {
    forwardEntity.nextQuestion(
      qIndex, questions, result, goForward
    );
  };
  const goNextGroup = () => {
    forwardEntity.nextGroup(gIndex, claims_groups, goForward);
  };
  const goNextRandomClaim = () => {
    forwardEntity.nextRandomClaim(rcIndex, random_claims, goForward);
  };
  const maleSelected = () => (result.answers.findIndex("S3_1") > -1);

  useLayoutEffect(() => {
    runPreload(data, changeLoadingState);
  }, []);

  return (
    <Fragment>
      <PageLoader loading={ loading || fraudProcessing } />
      <section className="survey-layout">
        {
          !step &&
          <Intro
            task={ translations.base_intro }
            nextStep={ goForward }
          />
        }
        {
          step === 2 &&
          <Screening
            question={ currentQuestion(demographics) }
            nextStep={ () => { goNextQuestion(demographics); } }
            result={ result }
          />
        }
        {
          step === 3 &&
          <Screening
            question={ currentQuestion(optional_demographics) }
            nextStep={ () => { goNextQuestion(optional_demographics); } }
            result={ result }
          />
        }
        {
          step === 4 &&
          <Screening
            question={ currentQuestion(screenings) }
            nextStep={ () => { goNextQuestion(screenings); } }
            result={ result }
          />
        }
        {
          step === 5 && !qualified_custom_text &&
          <Qualified
            title={ translations.express_qualify_title }
            descriptionTop={ translations.express_qualify_description_top }
            descriptionBottom={ translations.express_qualify_description_bottom }
            descriptionFooter={ translations.express_qualify_description_footer }
            nextStep={ goForward }
          />
        }
        {
          step === 5 && !!qualified_custom_text &&
          <CustomQualified
            content={ qualified_custom_text }
            nextStep={ goForward }
          />
        }
        {
          step === 6 &&
          <WithOpenEnd
            question={ currentQuestion(customMetricsByLocation(data.survey_metrics, "at_the_start")) }
            nextStep={ () => { goNextQuestion(customMetricsByLocation(data.survey_metrics, "at_the_start")); } }
            result={ result }
            mainResult={ result }
          />
        }
        {
          step === 7 &&
          <ProductIntro
            nextStep={ goForward }
            imageUrl={ image_url }
            description={ translations.claims_product_description }
            task={ task }
          />
        }
        {
          step === 8 &&
          <GroupIntro
            title={ translations.claims_intro_title }
            description={ translations.claims_intro_description }
            footer={ translations.claims_intro_footer }
            nextStep={ goForward }
          />
        }
        {
          step === 9 &&
          <Group
            desktopTitle={ translations.claims_groups_desktop }
            mobileTitle={ translations.claims_groups_mobile }
            group={ currentGroup() }
            mostLabel={ translations.claims_group_most }
            counter={ translations.claims_group_middle.replace('%{n}', gIndex + 1).replace('%{k}', claims_groups.length) }
            leastLabel={ translations.claims_group_least }
            result={ result }
            nextStep={ () => { goNextGroup(); } }
          />
        }
        {
          step === 10 &&
          <WithOpenEnd
            question={ currentQuestion(customMetricsByLocation(data.survey_metrics, "default")) }
            nextStep={ () => { goNextQuestion(customMetricsByLocation(data.survey_metrics, "default")); } }
            result={ result }
            mainResult={ result }
          />
        }
        {
          step === 11 &&
          <ThoughtBubbleIntro
            top={ translations.claims_thought_bubble_intro_top }
            bottom={ translations.claims_thought_bubble_intro_bottom.replace('%{n}', random_claims.length) }
            nextStep={ goForward }
          />
        }
        {
          step === 12 &&
          <ThoughtBubble
            concept={ currentRandomClaim() }
            tbKlass={ claim_klass }
            title={ translations.claims_thought_bubble_question.replace('%{claim}', currentRandomClaim().text) }
            description={ translations.claims_thought_bubble_description }
            showMan={ maleSelected() }
            result={ result }
            nextStep={ () => { goNextRandomClaim(); } }
          />
        }
        {
          step === 13 &&
          <Markup
            conceptIds={ markup_tool.concept_ids }
            markupTool={ markup_tool }
            nextStep={ goForward }
            result={ result }
          />
        }
        {
          step === 14 &&
          <WithOpenEnd
            question={ currentQuestion(customMetricsByLocation(data.survey_metrics, "at_the_end")) }
            nextStep={ () => { goNextQuestion(customMetricsByLocation(data.survey_metrics, "at_the_end")); } }
            result={ result }
            mainResult={ result }
          />
        }
        <LastStepsComponents
          step={ step }
          lastStepsFrom={ 15 }
          translations={ translations }
          data={ data }
          nextStep={ goForward }
        />
      </section>
    </Fragment>
  );
};

const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => {
  return {
    updateStep: (step) => { return dispatch({ type: "UPDATE_STEP", value: step }); },
    changeLoadingState: (value) => { return dispatch({ type: "UPDATE_LOADING", value }); },
    updateGroupIndex: (cIndex) => { return dispatch({ type: "UPDATE_GROUP_INDEX", value: cIndex }); },
    updateRandomClaimIndex: (tIndex) => { return dispatch({ type: "UPDATE_RANDOM_CLAIM_INDEX", value: tIndex }); },
    updateQuestionIndex: (qIndex) => { return dispatch({ type: "UPDATE_QUESTION_INDEX", value: qIndex }); }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Index);
